import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Power,
  Phone,
  Mail,
  MapPin,
  Hash,
  Building2,
  Users,
  ShieldCheck,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";
import { getBranch, deleteBranch } from "../../api/branches";
import { Card, Button, Skeleton, ConfirmDialog, Avatar, EmptyState } from "../../components/ui";
import { StatusChip } from "../../components/Chips";
import BranchFormModal from "./BranchFormModal";

export default function BranchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await getBranch(id);
      setBranch(res.data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteBranch(id);
      navigate("/branches");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-3xl mx-auto">
          <Skeleton className="h-6 w-32" />
          <Card className="mt-5 p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <div className="flex items-center gap-4">
              <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32 sm:w-40" />
                <Skeleton className="h-4 w-20 sm:w-28" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (notFound || !branch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
          <EmptyState
            icon={AlertTriangle}
            title="Branch not found"
            description="This branch may have been removed or doesn't belong to your chain."
            action={
              <Link to="/branches">
                <Button variant="secondary" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  Back to branches
                </Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-3xl mx-auto">
        <Link to="/branches" className="mb-4 sm:mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors duration-200">
          <ArrowLeft size={15} /> Back to branches
        </Link>

        <Card className="p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar name={branch.name} size={48} className="bg-gradient-to-br from-emerald-500 to-green-600 text-white text-lg shadow-lg shadow-emerald-500/20" />
              <div>
                <h1 className="text-lg sm:text-headline-md text-gray-800 font-bold">{branch.name}</h1>
                <p className="text-xs sm:text-body-sm text-gray-500">
                  {branch.partnerId} · {branch.businessType}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 flex-1 sm:flex-none text-sm"
              >
                <Pencil size={15} className="hidden sm:inline mr-1" /> Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                className="text-white hover:bg-red-50 hover:text-red-700 flex-1 sm:flex-none text-sm bg-red-500"
              >
                <Power size={15} className="hidden sm:inline mr-1" /> Delete
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-emerald-100/60 pt-6">
            <InfoRow icon={Phone} label="Phone" value={`+91 ${branch.phone}`} />
            <InfoRow icon={ShieldCheck} label="Status" value={<StatusChip status={branch.status} />} />
            <InfoRow icon={Mail} label="Email" value={branch.email || "—"} />
            <InfoRow icon={Hash} label="GST number" value={branch.gstNumber || "—"} />
            <InfoRow icon={Building2} label="Registered business name" value={branch.registeredBusinessName || "—"} />
            <InfoRow
              icon={MapPin}
              label="Location"
              value={
                branch.location?.city
                  ? [branch.location.address, branch.location.city, branch.location.state, branch.location.pincode]
                      .filter(Boolean)
                      .join(", ")
                  : "—"
              }
            />
          </div>

          {/* Action Buttons Row */}
          <div className="mt-6 border-t border-emerald-100/60 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* View Staff Button */}
              <Link to={`/employees?partner=${branch._id}`}>
                <Button 
                  variant="outline" 
                  className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 text-sm flex items-center justify-center gap-2"
                >
                  <Users size={16} />
                  View Staff
                </Button>
              </Link>
              
              {/* View Complaints Button */}
              <Link to={`/complaints?partner=${branch._id}`}>
                <Button 
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 text-sm flex items-center justify-center gap-2"
                >
                  <MessageSquare size={16} />
                  View Complaints
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        <BranchFormModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          branch={branch}
          onSaved={() => {
            setEditOpen(false);
            load();
          }}
        />

        <ConfirmDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={confirmDelete}
          loading={deleting}
          danger
          title="Delete branch?"
          description={`${branch.name} will be permanently deleted. Its staff and complaint history will be lost.`}
          confirmLabel="Delete"
        />
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
        <Icon size={15} />
      </div>
      <div>
        <p className="text-xs sm:text-body-sm text-gray-500">{label}</p>
        <div className="mt-0.5 text-sm sm:text-body-md text-gray-800 font-medium">{value}</div>
      </div>
    </div>
  );
}